import { computed, ref } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";

import LocalForagePlugin, { sectors } from "@/plugins/LocalForagePlugin";
import { ContentfulSector, SectorMarketState } from "@tmi/types/entities";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

import type { DataSeries } from "@tmi/types/api/sector-rs-matrix";
import { refreshSectorsFromServer } from "@/services/data/sync/sector-sync-coordinator";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

const initialized = computed< boolean >( () => allSectors.value.length > 0 );
const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
const contentfulRefreshing = ref< boolean >( false );
const iiRefreshing = ref< boolean >( false );

const relativeStrengthLoading = ref< boolean >( false );

const defaultSectors = [] as unknown as ContentfulSector[];
const allSectors = ref< ContentfulSector[] >( defaultSectors );

const defaultRelativeStrengthHistory = [] as unknown as DataSeries[];
const rsHistory = ref< DataSeries[] >( defaultRelativeStrengthHistory );

const selectedSectorDatePoint = ref< SectorMarketState | undefined >();

export const useSectorsStore = defineStore( 'sectors', () => {
    const { authToken } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();

    function resetSectors(): void {
        allSectors.value = [];
    }

    function resetRelativeStrength(): void {
        rsHistory.value = defaultRelativeStrengthHistory;
        relativeStrengthLoading.value = false;
    }

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        resetSectors();
        resetRelativeStrength();

        loading.value = false;
        contentfulRefreshing.value = false;
        iiRefreshing.value = false;
    }

    async function initializeAsync(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Sectors ].initialize() exiting - already processing...' );
            return;
        }

        loading.value = true;

        const t0: number = performance.now();

        if ( typeof sectors === 'undefined' ) {
            // Wait for the driver to settle
            await LocalForagePlugin.initialize();
        }

        await sectors.ready();
        if ( await sectors.length() > 0 ) {
            const uniqueSectors = new Map< number, ContentfulSector >();
            // Clear the contents of the searchable collections
            allSectors.value.splice( 0 );

            // Iterate over all elements within IDB
            sectors.iterate( ( sector: ContentfulSector ) => {
                uniqueSectors.set( sector.id, sector );
                return;
            } ).then( () => {
                allSectors.value = [ ...( Array.from( uniqueSectors.values() ) ) ]
                    .sort( ( s1: ContentfulSector, s2: ContentfulSector ) =>
                        s1.name.toLowerCase().localeCompare( s2.name.toLowerCase() )
                    );

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allSectors.value.length } sectors in ${ t1 - t0 } ms` );
            }).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.message ) );
                reset();
            } ).finally( () => {
                // Clear the "loading" flag
                loading.value = false;
            } );
        } else {
            if ( contentfulRefreshing.value ) {
                return;
            }

            contentfulRefreshing.value = true;

            // Timing - testing to see how expensive each method is
            const t0: number = performance.now();

            refreshSectorsFromServer( authToken.value )
                .then( ( hydratedSectors: ContentfulSector[] ) => allSectors.value = [ ...hydratedSectors ] )
                .catch( ( err: any ) => {
                    logger.error( JSON.stringify( err.message ) );
                    reset();
                } )
                .finally( () => {
                    // Clear the "loading" flag
                    loading.value = false;
                    contentfulRefreshing.value = false;

                    const t1: number = performance.now();
                    logger.info( `Fetching & mapping ${ allSectors.value.length } tmi/ii sector items took ${ t1 - t0 } ms` );
                } );
        }
    }

    const favoriteSectors = computed< ContentfulSector[] >( () => {
        return allSectors.value.filter( ( sector: ContentfulSector ) => userStore.isFavoriteSector( sector.id ) );
    } );

    const selectedSector = computed< SectorMarketState | undefined >( () => selectedSectorDatePoint.value );

    function sectorBySymbol( symbol: string ): ContentfulSector | undefined {
        return allSectors.value.find( ( sector: ContentfulSector ): boolean => sector.symbol === symbol );
    }

    function sectorByTmiId( id: number ): ContentfulSector | undefined {
        return allSectors.value.find( ( sector: ContentfulSector ): boolean => sector.id === id );
    }

    function sectorById( id: string ): ContentfulSector | undefined {
        return allSectors.value.find( ( sector: ContentfulSector ): boolean => sector.contentfulId === id );
    }

    function setSelectedSector( sectorHistory: SectorMarketState ) {
        selectedSectorDatePoint.value = sectorHistory;
    }

    return {
        // ACTIONS
        initializeAsync,
        reset,
        sectorById,
        sectorByTmiId,
        sectorBySymbol,
        setSelectedSector,

        // GETTERS
        allSectors,
        favoriteSectors,
        isLoading,
        rsHistory,
        selectedSector
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSectorsStore, import.meta.hot ) );
}